/**
 * `/admin` → Activity → **Statistics**: plots, exports and page visits in
 * numbers, from `audit.usage_event`.
 *
 * Top to bottom: totals for the period, plots per day, the most used plot
 * types, ships, signals and pages, and a row per person. A person's row opens
 * their plots on the right as readable rows, each with **Open in Explore**,
 * which plots the same request again in a new tab (`usage-plot.ts`).
 *
 * Gated by `audit:read` like the log; the server asks again.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { getActivityStats, listPersonPlots, type ActivityPlot, type ActivityStats } from '../../api/endpoints.ts';
import { userMessage } from '../../api/errors.ts';
import { timeAgo } from '../../utils/dates.ts';
import { shipLabel } from '../../utils/ships.ts';
import { fillDays } from './transcripts-usage.ts';
import { openRequestInExplore, pageLabel, signalsOf } from './usage-plot.ts';

const PERIODS: Array<{ days: number; label: string }> = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 0, label: 'All' },
];
const PAGE = 100;

type Person = ActivityStats['people'][number];

function dayLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** `2026-10-01T00:00:00+00:00` → `1 Oct 2026 00:00`, in UTC like the request. */
function utc(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })} ${d.toISOString().slice(11, 16)}`;
}

function seconds(ms: number | null | undefined): string {
  if (ms == null) return '';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

@customElement('activity-stats')
export class ActivityStatsView extends LightElement {
  @state() private days = 30;
  @state() private stats: ActivityStats | null = null;
  @state() private loading = false;
  @state() private error = '';
  @state() private hoverDay: string | null = null;
  @state() private person: Person | null = null;
  @state() private plots: ActivityPlot[] = [];
  @state() private plotsLoading = false;
  @state() private plotsError = '';
  @state() private more = false;
  private rev = 0;
  private plotsRev = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.rev++;
    this.plotsRev++;
  }

  private async load(): Promise<void> {
    const rev = ++this.rev;
    this.loading = true;
    this.error = '';
    try {
      const stats = await getActivityStats(this.days);
      if (rev === this.rev) this.stats = stats;
    } catch (e) {
      if (rev === this.rev) this.error = userMessage(e);
    } finally {
      if (rev === this.rev) this.loading = false;
    }
    if (rev === this.rev && this.person) void this.loadPlots(this.person, false);
  }

  private async loadPlots(person: Person, append: boolean): Promise<void> {
    const rev = ++this.plotsRev;
    this.plotsLoading = true;
    this.plotsError = '';
    try {
      const page = await listPersonPlots(person.id, this.days, append ? this.plots.length : 0);
      if (rev !== this.plotsRev) return;
      this.plots = append ? [...this.plots, ...page] : page;
      this.more = page.length === PAGE;
    } catch (e) {
      if (rev === this.plotsRev) this.plotsError = userMessage(e);
    } finally {
      if (rev === this.plotsRev) this.plotsLoading = false;
    }
  }

  private setPeriod(days: number): void {
    if (days === this.days) return;
    this.days = days;
    void this.load();
  }

  private openPerson(p: Person): void {
    if (this.person?.id === p.id) {
      this.person = null;
      return;
    }
    this.person = p;
    this.plots = [];
    void this.loadPlots(p, false);
  }

  private renderTiles(s: ActivityStats): TemplateResult {
    const t = s.totals!;
    const tile = (label: string, value: string, tone = '') => html`<div class="usage-tile ${tone}">
      <span class="usage-tile-label">${label}</span>
      <span class="usage-tile-value">${value}</span>
    </div>`;
    return html`<div class="usage-tiles">
      ${tile('Plots', String(t.plots))}
      ${tile('People', String(t.people))}
      ${tile('Failed', String(t.errors), t.errors ? 'is-error' : '')}
      ${tile('Median plot time', t.median_ms == null ? '-' : seconds(Math.round(t.median_ms)))}
      ${tile('Exports', String(t.exports))}
      ${tile('Page visits', String(t.visits))}
    </div>`;
  }

  private renderDays(s: ActivityStats): TemplateResult {
    const days = fillDays(s.per_day, s.days ?? null, (day) => ({ day, plots: 0, errors: 0 }));
    const max = Math.max(1, ...days.map((d) => d.plots));
    const say = (d: (typeof days)[number]) =>
      `${dayLabel(d.day)}: ${d.plots} plot${d.plots === 1 ? '' : 's'}${d.errors ? `, ${d.errors} failed` : ''}`;
    const hovered = days.find((d) => d.day === this.hoverDay);
    return html`<section class="usage-section" aria-label="Plots per day">
      <div class="usage-days-head">
        <span class="usage-readout" aria-live="polite">${hovered ? say(hovered) : 'Plots per day'}</span>
        <span class="usage-days-max">${max}</span>
      </div>
      <div class="usage-bars" @mouseleave=${() => { this.hoverDay = null; }}>
        ${days.map((d) => html`<div class="usage-bar-slot ${d.day === this.hoverDay ? 'is-hover' : ''}" title=${say(d)}
            @mouseenter=${() => { this.hoverDay = d.day; }}>
            ${d.plots ? html`<div class="usage-bar" style=${`height:${(d.plots / max) * 100}%`}></div>` : nothing}
          </div>`)}
      </div>
      <div class="usage-days-axis">
        <span>${days.length ? dayLabel(days[0]!.day) : ''}</span>
        <span>${days.length ? dayLabel(days[days.length - 1]!.day) : ''}</span>
      </div>
    </section>`;
  }

  private renderTop(title: string, rows: ActivityStats['plot_types'], label: (name: string) => string = (n) => n): TemplateResult {
    const max = Math.max(1, ...rows.map((r) => r.count));
    return html`<div class="act-top">
      <h3>${title}</h3>
      ${rows.length
        ? html`<ol>${rows.map((r) => html`<li title=${r.name}>
            <span class="act-top-name">${label(r.name)}</span>
            <span class="act-top-count">${r.count}</span>
            <i style=${`width:${(r.count / max) * 100}%`}></i>
          </li>`)}</ol>`
        : html`<p class="people-muted">Nothing yet.</p>`}
    </div>`;
  }

  private renderPeople(s: ActivityStats): TemplateResult {
    if (!s.people.length) return html`<p class="transcripts-note">Nobody plotted anything in this period.</p>`;
    return html`<div class="people-table-wrap usage-people"><table class="people-table">
      <thead><tr>
        <th>Person</th>
        <th class="num">Plots</th>
        <th class="num">Failed</th>
        <th class="num">Exports</th>
        <th class="num">Visits</th>
        <th>Last seen</th>
      </tr></thead>
      <tbody>
        ${s.people.map((p) => html`<tr class="usage-row ${this.person?.id === p.id ? 'is-open' : ''}" tabindex="0"
            title="Show what they plotted" aria-pressed=${this.person?.id === p.id}
            @click=${() => this.openPerson(p)}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.openPerson(p); }}>
          <td><span class="usage-person">${p.display_name ?? 'Unknown person'}</span>${p.email ? html`<span class="people-muted">${p.email}</span>` : nothing}</td>
          <td class="num">${p.plots || '-'}</td>
          <td class="num">${p.errors || '-'}</td>
          <td class="num">${p.exports || '-'}</td>
          <td class="num">${p.visits || '-'}</td>
          <td><time datetime=${p.last_at}>${timeAgo(p.last_at, 'long')}</time></td>
        </tr>`)}
      </tbody>
    </table></div>`;
  }

  private renderPlot(p: ActivityPlot): TemplateResult {
    const signals = signalsOf(p.request);
    const when = new Date(p.occurred_at);
    const canOpen = p.kind === 'plot' && !!p.plot_type && !!p.ship_id;
    return html`<li class="act-plot ${p.outcome !== 'success' ? 'is-error' : ''}">
      <div class="act-plot-head">
        <time datetime=${p.occurred_at} title=${when.toISOString()}>
          ${when.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </time>
        <span class="act-plot-type">${p.kind === 'export' ? `Export · ${String(p.request.format ?? '')}` : p.plot_type}</span>
        <span>${p.ship_id ? shipLabel(p.ship_id) : ''}</span>
        ${p.source === 'live' ? html`<span class="admin-kind">live</span>` : nothing}
        <span class="people-muted act-plot-page">${pageLabel(p.page)}</span>
      </div>
      <div class="act-plot-body">
        <span>${utc(p.start_at)} - ${utc(p.end_at)} UTC</span>
        ${signals.length ? html`<span class="act-plot-signals" title=${signals.join(', ')}>${signals.join(', ')}</span>` : nothing}
        <span class="people-muted">${[
          p.row_count != null ? `${p.row_count.toLocaleString('en-GB')} rows` : '',
          seconds(p.duration_ms),
        ].filter(Boolean).join(' · ')}</span>
        ${p.error ? html`<span class="act-plot-error">${p.error}</span>` : nothing}
      </div>
      ${canOpen
        ? html`<button type="button" class="btn btn-secondary act-plot-open" @click=${() => openRequestInExplore(p.request)}>
            Open in Explore ↗
          </button>`
        : nothing}
    </li>`;
  }

  private renderPerson(p: Person): TemplateResult {
    return html`<aside class="act-person" aria-label=${`What ${p.display_name ?? 'this person'} plotted`}>
      <div class="act-person-head">
        <h3>${p.display_name ?? 'Unknown person'} <span class="people-muted">· ${p.plots + p.exports} plots and exports</span></h3>
        <button type="button" class="icon-btn" aria-label="Close" @click=${() => { this.person = null; }}>×</button>
      </div>
      ${this.plotsError
        ? html`<p class="transcripts-note is-error" role="alert">${this.plotsError}</p>`
        : !this.plots.length
          ? html`<p class="transcripts-note">${this.plotsLoading ? 'Loading…' : 'No plots in this period.'}</p>`
          : html`<ol class="act-plots">${this.plots.map((x) => this.renderPlot(x))}</ol>
              ${this.more
                ? html`<button type="button" class="catalog-btn" ?disabled=${this.plotsLoading}
                    @click=${() => void this.loadPlots(p, true)}>Older</button>`
                : nothing}`}
    </aside>`;
  }

  override render(): TemplateResult {
    const s = this.stats;
    return html`<div class="usage" aria-busy=${this.loading}>
      <div class="usage-head">
        <div class="seg" role="group" aria-label="Period">
          ${PERIODS.map((p) => html`<button type="button" class="seg-btn ${p.days === this.days ? 'active' : ''}"
            aria-pressed=${p.days === this.days} @click=${() => this.setPeriod(p.days)}>${p.label}</button>`)}
        </div>
        <span class="people-muted">Automatic Live refreshes are not counted. Times in UTC.</span>
      </div>
      ${this.error
        ? html`<p class="transcripts-note is-error" role="alert">${this.error}</p>`
        : !s
          ? html`<p class="transcripts-note">Loading…</p>`
          : !s.available
            ? html`<p class="transcripts-note">The usage log needs the metadata database.</p>`
            : html`${this.renderTiles(s)} ${this.renderDays(s)}
                <div class="act-tops">
                  ${this.renderTop('Plot types', s.plot_types)}
                  ${this.renderTop('Ships', s.ships, shipLabel)}
                  ${this.renderTop('Signals', s.signals)}
                  ${this.renderTop('Pages visited', s.pages, pageLabel)}
                </div>
                <div class="act-people ${this.person ? 'has-person' : ''}">
                  ${this.renderPeople(s)} ${this.person ? this.renderPerson(this.person) : nothing}
                </div>`}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'activity-stats': ActivityStatsView;
  }
}
